import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Container, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import {
  getWaitlist,
  updateWaitlistStatus,
  fetchBranches,
  bulkCancelWaitlist,
  bulkRequoteWaitlist,
  updateBranchWaitlistSettings,
  seatWaitlistEntry,
  getTables,
} from "../../../utils/apiCalls";
import { exportToCsv } from "../../../utils/exportCsv";
import { C, tbl, reportingGlobalCSS } from "../../reporting/reportingStyles";
import { Tile, RCard, Badge, Loader } from "../../reporting/ReportingShared";
import Breadcrumb from "../../common/breadcrumb";

const NOTIFY_BEFORE_MINUTES = 2;

const CANCEL_REASONS = [
  { value: "customer_left", label: "Customer left" },
  { value: "staff_cancelled", label: "Staff cancelled" },
  { value: "no_longer_needed", label: "No longer needed" },
  { value: "duplicate", label: "Duplicate" },
  { value: "other", label: "Other" },
];

const getWaitTimeInfo = (entry) => {
  if (!entry.quotedWaitMinutes || !entry.createdAt) return null;
  if (entry.status !== "waiting" && entry.status !== "notified") return null;
  const deadline = new Date(entry.createdAt).getTime() + entry.quotedWaitMinutes * 60 * 1000;
  const remaining = deadline - Date.now();
  const remainingMin = Math.ceil(remaining / (1000 * 60));
  if (remaining <= 0) {
    const overdueMin = Math.abs(remainingMin);
    return { label: `Overdue by ${overdueMin}m`, color: C.coral, bg: C.coralLight, shouldNotify: overdueMin <= 10 };
  }
  if (remainingMin <= 5) return { label: `${remainingMin}m left`, color: C.orange, bg: C.orangeLight, shouldNotify: remainingMin <= NOTIFY_BEFORE_MINUTES };
  return { label: `${remainingMin}m left`, color: C.green, bg: C.greenLight, shouldNotify: false };
};

const WaitlistList = () => {
  const navigate = useNavigate();
  const userType = localStorage.getItem("userType");
  const merchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = (userType === "merchant" || userType === "waitlist_staff") ? merchantId : reduxSelectedMerchant;

  const [branches, setBranches] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ waiting: 0, notified: 0, seated: 0, cancelled: 0, no_show: 0 });
  const [timezone, setTimezone] = useState("Asia/Riyadh");
  const [, setTick] = useState(0);
  const notifiedIds = useRef(new Set(JSON.parse(sessionStorage.getItem("waitlist_notified_ids") || "[]")));

  // Modal state — only one modal at a time
  const [modal, setModal] = useState(null); // { type: 'cancel' | 'bulkCancel' | 'requote' | 'settings' | 'seat', entry?, ... }

  // Per-modal form state
  const [cancelReason, setCancelReason] = useState("customer_left");
  const [requoteDelta, setRequoteDelta] = useState(15);
  const [seatTables, setSeatTables] = useState([]);
  const [seatTableId, setSeatTableId] = useState("");
  const [seatLoading, setSeatLoading] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    waitlistPaused: false,
    waitlistCapacityCap: "",
    waitlistQuietFrom: "",
    waitlistQuietTo: "",
    waitlistCurrentWaitMinutes: "",
    tableAutoReleaseHours: "",
  });

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") Notification.requestPermission();
  }, []);

  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const checkNotifications = useCallback(() => {
    for (const w of waitlist) {
      const info = getWaitTimeInfo(w);
      if (info?.shouldNotify && !notifiedIds.current.has(w.id)) {
        notifiedIds.current.add(w.id);
        sessionStorage.setItem("waitlist_notified_ids", JSON.stringify([...notifiedIds.current]));
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification("Waitlist Alert", { body: `${w.customerName} (party of ${w.partySize}) — ${info.label}`, icon: "/favicon.ico" });
        }
      }
    }
  }, [waitlist]);

  useEffect(() => {
    const interval = setInterval(() => { setTick((t) => t + 1); checkNotifications(); }, 15000);
    return () => clearInterval(interval);
  }, [checkNotifications]);

  useEffect(() => { if (waitlist.length) checkNotifications(); }, [waitlist, checkNotifications]);

  useEffect(() => {
    const load = async () => {
      const response = await fetchBranches({}, false);
      let list = response?.data || [];
      if (selectedMerchant && selectedMerchant !== "all") list = list.filter((b) => String(b.MerchantId) === String(selectedMerchant));
      // Waitlist staff: restrict the branch picker to their assigned branches.
      if (userType === "waitlist_staff") {
        const assigned = JSON.parse(localStorage.getItem("assignedBranchIds") || "[]").map(String);
        list = list.filter((b) => assigned.includes(String(b.id)));
      }
      setBranches(list);
    };
    load();
  }, [selectedMerchant, userType]);

  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (!selectedMerchant || selectedMerchant === "all") { setWaitlist([]); return; }
    if (!silent) setIsLoading(true);
    const params = { MerchantId: selectedMerchant, page: currentPage, limit: 20 };
    if (selectedBranch) params.branchId = selectedBranch;
    if (statusFilter) params.status = statusFilter;
    if (search.trim()) params.search = search.trim();
    const data = await getWaitlist(params);
    setWaitlist(data?.rows || []);
    setTotalPages(data?.totalPages || 0);
    setTotal(data?.total || 0);
    if (data?.counts) setCounts(data.counts);
    if (data?.timezone) setTimezone(data.timezone);
    if (!silent) setIsLoading(false);
  }, [selectedMerchant, selectedBranch, statusFilter, search, currentPage]);

  const formatBranchTime = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: timezone,
      });
    } catch {
      return new Date(iso).toLocaleTimeString();
    }
  };

  const minutesSince = (iso) => {
    if (!iso) return null;
    const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    return m < 0 ? 0 : m;
  };

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setCurrentPage(1); }, [selectedBranch, statusFilter, search]);

  useEffect(() => {
    const interval = setInterval(() => { fetchData({ silent: true }); }, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const closeModal = () => {
    setModal(null);
    setSeatTables([]);
    setSeatTableId("");
    setCancelReason("customer_left");
  };

  const openCancel = (entry) => { setCancelReason("customer_left"); setModal({ type: "cancel", entry }); };
  const openBulkCancel = () => { setCancelReason("staff_cancelled"); setModal({ type: "bulkCancel" }); };
  const openRequote = () => { setRequoteDelta(15); setModal({ type: "requote" }); };

  const openSettings = () => {
    const branch = branches.find((b) => String(b.id) === String(selectedBranch));
    setSettingsForm({
      waitlistPaused: !!branch?.waitlistPaused,
      waitlistCapacityCap: branch?.waitlistCapacityCap ?? "",
      waitlistQuietFrom: branch?.waitlistQuietFrom ?? "",
      waitlistQuietTo: branch?.waitlistQuietTo ?? "",
      waitlistCurrentWaitMinutes: branch?.waitlistCurrentWaitMinutes ?? "",
      tableAutoReleaseHours: branch?.tableAutoReleaseHours ?? "",
    });
    setModal({ type: "settings" });
  };

  const openSeat = async (entry) => {
    setSeatTableId("");
    setSeatTables([]);
    setModal({ type: "seat", entry });
    if (entry.branchId) {
      const t = await getTables({ branchId: entry.branchId });
      setSeatTables((t || []).filter((row) => row.status === "available"));
    }
  };

  const handleNotify = async (id) => { const r = await updateWaitlistStatus(id, { status: "notified" }); if (r) fetchData(); };

  const handleConfirmCancel = async () => {
    if (!modal?.entry) return;
    const r = await updateWaitlistStatus(modal.entry.id, { status: "cancelled", cancelReason });
    if (r) { closeModal(); fetchData(); }
  };

  const handleConfirmBulkCancel = async () => {
    const payload = { MerchantId: Number(selectedMerchant) };
    if (selectedBranch) payload.branchId = Number(selectedBranch);
    const r = await bulkCancelWaitlist(payload);
    if (r) { closeModal(); fetchData(); }
  };

  const handleConfirmRequote = async () => {
    const delta = Number(requoteDelta);
    if (!Number.isInteger(delta) || delta === 0) return;
    const payload = { MerchantId: Number(selectedMerchant), deltaMinutes: delta };
    if (selectedBranch) payload.branchId = Number(selectedBranch);
    const r = await bulkRequoteWaitlist(payload);
    if (r) { closeModal(); fetchData(); }
  };

  const handleConfirmSeat = async () => {
    if (!modal?.entry || !seatTableId) return;
    setSeatLoading(true);
    const r = await seatWaitlistEntry(modal.entry.id, { tableId: Number(seatTableId) });
    setSeatLoading(false);
    if (r) { closeModal(); fetchData(); }
  };

  const handleSaveSettings = async () => {
    if (!selectedBranch) return;
    const payload = {
      waitlistPaused: !!settingsForm.waitlistPaused,
      waitlistCapacityCap: settingsForm.waitlistCapacityCap === "" ? null : Number(settingsForm.waitlistCapacityCap),
      waitlistQuietFrom: settingsForm.waitlistQuietFrom || null,
      waitlistQuietTo: settingsForm.waitlistQuietTo || null,
      waitlistCurrentWaitMinutes: settingsForm.waitlistCurrentWaitMinutes === "" ? null : Number(settingsForm.waitlistCurrentWaitMinutes),
      tableAutoReleaseHours: settingsForm.tableAutoReleaseHours === "" ? null : Number(settingsForm.tableAutoReleaseHours),
    };
    const r = await updateBranchWaitlistSettings(Number(selectedBranch), payload);
    if (r) {
      // Refresh branches so the stored values match the server
      setBranches((prev) => prev.map((b) => (String(b.id) === String(selectedBranch) ? { ...b, ...r } : b)));
      closeModal();
    }
  };

  const handleExport = () => {
    exportToCsv("waitlist.csv", waitlist, [
      { key: (w, i) => i + 1, label: "#" }, { key: "customerName", label: "Customer" }, { key: "customerPhone", label: "Phone" },
      { key: "partySize", label: "Party" }, { key: (w) => w.Branch?.name || "", label: "Branch" },
      { key: "quotedWaitMinutes", label: "Est. Wait" }, { key: (w) => formatBranchTime(w.createdAt), label: "Joined" }, { key: "status", label: "Status" },
    ]);
  };

  const tiles = [
    { label: "Waiting", value: counts.waiting, gradient: `linear-gradient(135deg, ${C.orange}, #FFD200)`, icon: "⏳", delay: 0 },
    { label: "Notified", value: counts.notified, gradient: `linear-gradient(135deg, ${C.blue}, #A8B8FF)`, icon: "📢", delay: 80 },
    { label: "Seated", value: counts.seated, gradient: `linear-gradient(135deg, ${C.green}, #38F9D7)`, icon: "✓", delay: 160 },
    { label: "No Shows", value: counts.no_show, gradient: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, icon: "✕", delay: 240 },
  ];

  const statusBadgeColor = (s) => {
    if (s === "waiting") return { color: C.orange, bg: C.orangeLight };
    if (s === "notified") return { color: C.blue, bg: C.blueLight };
    if (s === "seated") return { color: C.green, bg: C.greenLight };
    return { color: C.coral, bg: C.coralLight };
  };

  const sourceLabel = (s) => {
    if (s === "customer_qr") return "QR";
    if (s === "reservation_conversion") return "Reservation";
    if (s === "staff_admin") return "Staff";
    return null;
  };

  const settingsDisabledHint = !selectedBranch ? "Select a branch to configure waitlist settings" : null;

  return (
    <Fragment>
      <style>{reportingGlobalCSS}</style>
      <Breadcrumb title="Waitlist" parent="Table Management" />
      <Container fluid className="rpt-wrap">
        {/* Filters */}
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: isMobile ? 10 : 20, flexWrap: "wrap" }}>
          <Input type="select" value={selectedBranch} onChange={(e) => setSelectedBranch(e.target.value)} style={{ maxWidth: isMobile ? "100%" : 200, flex: isMobile ? "1 1 100%" : "0 0 auto", borderRadius: 8, border: `1px solid ${C.border}` }}>
            <option value="">All Branches</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Input>
          <Input type="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ maxWidth: isMobile ? "100%" : 180, flex: isMobile ? "1 1 48%" : "0 0 auto", borderRadius: 8, border: `1px solid ${C.border}` }}>
            <option value="">Active (Waiting/Notified)</option>
            {["waiting", "notified", "seated", "cancelled", "no_show"].map((s) => <option key={s} value={s}>{s}</option>)}
          </Input>
          <Input type="text" placeholder="Search name or phone..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: isMobile ? "100%" : 220, flex: isMobile ? "1 1 48%" : "0 0 auto", borderRadius: 8, border: `1px solid ${C.border}` }} />
          {!isMobile && <div style={{ flex: 1 }} />}

          {!isMobile && (
            <>
              {userType !== "waitlist_staff" && (
                <Button size="sm" title={settingsDisabledHint || "Waitlist settings for this branch"} disabled={!selectedBranch} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600, fontSize: 12, opacity: selectedBranch ? 1 : 0.5 }} onClick={openSettings}>
                  <i className="fa fa-cog" /> Settings
                </Button>
              )}
              {waitlist.length > 0 && (
                <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600, fontSize: 12 }} onClick={handleExport}>
                  <i className="fa fa-download" /> CSV
                </Button>
              )}
              {counts.waiting + counts.notified > 0 && (
                <>
                  <Button size="sm" style={{ background: `linear-gradient(135deg, ${C.blue}, #A8B8FF)`, border: "none", borderRadius: 8, fontWeight: 600, fontSize: 12 }} onClick={openRequote}>Re-quote</Button>
                  <Button size="sm" style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontWeight: 600, fontSize: 12 }} onClick={openBulkCancel}>Cancel All</Button>
                </>
              )}
              <Button size="sm" style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontWeight: 600, fontSize: 12, padding: "8px 16px" }} onClick={() => navigate("/table-management/add-waitlist")}>
                + Add to Waitlist
              </Button>
            </>
          )}
        </div>

        {/* Mobile: action buttons in a horizontal scroll strip */}
        {isMobile && (
          <div style={{ display: "flex", gap: 8, overflowX: "auto", marginBottom: 12, paddingBottom: 4, WebkitOverflowScrolling: "touch" }}>
            <Button size="sm" style={{ flex: "0 0 auto", whiteSpace: "nowrap", background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontWeight: 600, fontSize: 12, padding: "8px 14px" }} onClick={() => navigate("/table-management/add-waitlist")}>
              + Add
            </Button>
            {userType !== "waitlist_staff" && (
              <Button size="sm" title={settingsDisabledHint || "Waitlist settings for this branch"} disabled={!selectedBranch} style={{ flex: "0 0 auto", whiteSpace: "nowrap", background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600, fontSize: 12, opacity: selectedBranch ? 1 : 0.5 }} onClick={openSettings}>
                <i className="fa fa-cog" /> Settings
              </Button>
            )}
            {waitlist.length > 0 && (
              <Button size="sm" style={{ flex: "0 0 auto", whiteSpace: "nowrap", background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontWeight: 600, fontSize: 12 }} onClick={handleExport}>
                <i className="fa fa-download" /> CSV
              </Button>
            )}
            {counts.waiting + counts.notified > 0 && (
              <>
                <Button size="sm" style={{ flex: "0 0 auto", whiteSpace: "nowrap", background: `linear-gradient(135deg, ${C.blue}, #A8B8FF)`, border: "none", borderRadius: 8, fontWeight: 600, fontSize: 12 }} onClick={openRequote}>Re-quote</Button>
                <Button size="sm" style={{ flex: "0 0 auto", whiteSpace: "nowrap", background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontWeight: 600, fontSize: 12 }} onClick={openBulkCancel}>Cancel All</Button>
              </>
            )}
          </div>
        )}

        {/* Stat Tiles */}
        {(selectedMerchant && selectedMerchant !== "all") && (
          isMobile ? (
            <div style={{ display: "flex", gap: 10, overflowX: "auto", marginBottom: 16, paddingBottom: 4, WebkitOverflowScrolling: "touch" }}>
              {tiles.map((t) => (
                <div key={t.label} style={{ flex: "0 0 160px" }}>
                  <Tile {...t} />
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 20 }}>
              {tiles.map((t) => <Tile key={t.label} {...t} />)}
            </div>
          )
        )}

        {/* Table */}
        <RCard style={{ padding: 0, overflow: "hidden" }}>
          {isLoading ? <Loader /> : (!selectedMerchant || selectedMerchant === "all") ? (
            <p style={{ textAlign: "center", padding: 40, color: C.textLight }}>Select a merchant</p>
          ) : waitlist.length === 0 ? (
            <p style={{ textAlign: "center", padding: 40, color: C.textLight }}>No waitlist entries</p>
          ) : isMobile ? (
            <>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 12 }}>
                {waitlist.map((w) => {
                  const timeInfo = getWaitTimeInfo(w);
                  const sb = statusBadgeColor(w.status);
                  const isActive = w.status === "waiting" || w.status === "notified";
                  const positionLabel = w.position ? `#${w.position}` : "—";
                  return (
                    <div
                      key={w.id}
                      style={{
                        background: C.white,
                        border: `1px solid ${C.borderLight}`,
                        borderLeft: `4px solid ${sb.color}`,
                        borderRadius: 10,
                        padding: 12,
                        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 14, fontWeight: 800, color: C.coral }}>{positionLabel}</span>
                          <div>
                            <div style={{ fontWeight: 700, color: C.text, fontSize: 14 }}>{w.customerName}</div>
                            {w.customerPhone && <div style={{ fontSize: 11, color: C.textLight }}>{w.customerPhone}</div>}
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <Badge color={sb.color} bg={sb.bg}>{w.status}</Badge>
                          {sourceLabel(w.source) && (
                            <div style={{ fontSize: 9, fontWeight: 700, color: C.textMed, marginTop: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>
                              {sourceLabel(w.source)}
                            </div>
                          )}
                        </div>
                      </div>

                      {w.notes && (
                        <div style={{ fontSize: 12, color: C.textMed, fontStyle: "italic", marginBottom: 8, padding: 6, background: C.borderLight, borderRadius: 6 }}>
                          <i className="fa fa-sticky-note" style={{ marginRight: 6, color: C.orange }} />
                          {w.notes}
                        </div>
                      )}

                      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, fontSize: 12, color: C.textMed, marginBottom: 8 }}>
                        <span><strong style={{ color: C.text }}>{w.partySize}</strong> {w.partySize === 1 ? "guest" : "guests"}</span>
                        <span>·</span>
                        <span>{w.Branch?.name || "—"}</span>
                        <span>·</span>
                        <span>Joined {formatBranchTime(w.createdAt)}</span>
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 10 }}>
                        <span style={{ fontSize: 12, color: C.textLight }}>
                          Est. {w.quotedWaitMinutes ? `${w.quotedWaitMinutes} min` : "—"}
                        </span>
                        {timeInfo && <Badge color={timeInfo.color} bg={timeInfo.bg}>{timeInfo.label}</Badge>}
                      </div>

                      {w.status === "notified" && w.notifiedAt && (
                        <div style={{ fontSize: 11, color: C.textLight, marginBottom: 8 }} title={formatBranchTime(w.notifiedAt)}>
                          Notified {minutesSince(w.notifiedAt)}m ago
                        </div>
                      )}

                      {isActive && (
                        <div style={{ display: "flex", gap: 8 }}>
                          {w.status === "waiting" && (
                            <Button style={{ flex: 1, background: `linear-gradient(135deg, ${C.blue}, #A8B8FF)`, border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, padding: "10px 0" }} onClick={() => handleNotify(w.id)}>Notify</Button>
                          )}
                          <Button style={{ flex: 1, background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, padding: "10px 0" }} onClick={() => openSeat(w)}>Seat</Button>
                          <Button style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.textMed, fontSize: 13, padding: "10px 14px" }} onClick={() => navigate(`/table-management/edit-waitlist/${w.id}`)}>
                            <i className="fa fa-edit" />
                          </Button>
                          <Button style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 8, color: C.coral, fontSize: 13, padding: "10px 14px" }} onClick={() => openCancel(w)}>
                            <i className="fa fa-times" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {totalPages > 1 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: `1px solid ${C.borderLight}` }}>
                  <span style={{ fontSize: 12, color: C.textLight }}>{(currentPage - 1) * 20 + 1}–{Math.min(currentPage * 20, total)} / {total}</span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMed, fontWeight: 600, fontSize: 12 }} disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>Prev</Button>
                    <span style={{ padding: "6px 8px", fontSize: 12, color: C.textMed }}>{currentPage}/{totalPages}</span>
                    <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMed, fontWeight: 600, fontSize: 12 }} disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      {["#", "Customer", "Party", "Branch", "Est. Wait", "Time Left", "Joined", "Status", "Actions"].map((h) => (
                        <th key={h} style={{ ...tbl.th, ...(h === "Actions" && { textAlign: "right" }) }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {waitlist.map((w, idx) => {
                      const timeInfo = getWaitTimeInfo(w);
                      const sb = statusBadgeColor(w.status);
                      const isActive = w.status === "waiting" || w.status === "notified";
                      const positionLabel = w.position ? `#${w.position}` : "—";
                      return (
                        <tr key={w.id} className="rpt-row">
                          <td style={tbl.td}><span style={{ fontSize: 16, fontWeight: 800, color: C.coral }}>{positionLabel}</span></td>
                          <td style={tbl.td}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <span style={{ fontWeight: 600, color: C.text }}>{w.customerName}</span>
                              {sourceLabel(w.source) && (
                                <span style={{ fontSize: 9, fontWeight: 700, color: C.textMed, background: C.borderLight, padding: "1px 6px", borderRadius: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>
                                  {sourceLabel(w.source)}
                                </span>
                              )}
                            </div>
                            {w.customerPhone && <div style={{ fontSize: 11, color: C.textLight }}>{w.customerPhone}</div>}
                            {w.notes && (
                              <div
                                title={w.notes}
                                style={{ fontSize: 11, color: C.textMed, fontStyle: "italic", marginTop: 2, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                              >
                                <i className="fa fa-sticky-note" style={{ marginRight: 4, color: C.orange }} />
                                {w.notes}
                              </div>
                            )}
                          </td>
                          <td style={{ ...tbl.td, fontWeight: 700 }}>{w.partySize}</td>
                          <td style={tbl.td}>{w.Branch?.name || "—"}</td>
                          <td style={tbl.td}>{w.quotedWaitMinutes ? `${w.quotedWaitMinutes} min` : "—"}</td>
                          <td style={tbl.td}>
                            {timeInfo ? <Badge color={timeInfo.color} bg={timeInfo.bg}>{timeInfo.label}</Badge> : "—"}
                          </td>
                          <td style={tbl.td}>{formatBranchTime(w.createdAt)}</td>
                          <td style={tbl.td}>
                            <Badge color={sb.color} bg={sb.bg}>{w.status}</Badge>
                            {w.status === "notified" && w.notifiedAt && (
                              <div
                                style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}
                                title={formatBranchTime(w.notifiedAt)}
                              >
                                Notified {minutesSince(w.notifiedAt)}m ago
                              </div>
                            )}
                          </td>
                          <td style={{ ...tbl.td, textAlign: "right" }}>
                            <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                              {w.status === "waiting" && (
                                <Button size="sm" style={{ background: `linear-gradient(135deg, ${C.blue}, #A8B8FF)`, border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700 }} onClick={() => handleNotify(w.id)}>Notify</Button>
                              )}
                              {isActive && (
                                <Button size="sm" style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700 }} onClick={() => openSeat(w)}>Seat</Button>
                              )}
                              {isActive && (
                                <Button
                                  size="sm"
                                  title="Open full entry"
                                  style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMed, fontSize: 11, fontWeight: 700 }}
                                  onClick={() => navigate(`/table-management/edit-waitlist/${w.id}`)}
                                >
                                  <i className="fa fa-edit" />
                                </Button>
                              )}
                              {isActive && (
                                <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.coral, fontSize: 11, fontWeight: 700 }} onClick={() => openCancel(w)}>
                                  <i className="fa fa-times" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderTop: `1px solid ${C.borderLight}` }}>
                  <span style={{ fontSize: 12, color: C.textLight }}>Showing {(currentPage - 1) * 20 + 1}–{Math.min(currentPage * 20, total)} of {total}</span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMed, fontWeight: 600, fontSize: 12 }} disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>Previous</Button>
                    <span style={{ padding: "6px 12px", fontSize: 12, color: C.textMed }}>{currentPage} / {totalPages}</span>
                    <Button size="sm" style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMed, fontWeight: 600, fontSize: 12 }} disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>Next</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </RCard>
      </Container>

      {/* Cancel single entry */}
      <Modal isOpen={modal?.type === "cancel"} toggle={closeModal} centered>
        <ModalHeader toggle={closeModal}>Cancel waitlist entry</ModalHeader>
        <ModalBody>
          <p style={{ color: C.textMed, marginBottom: 12 }}>
            Cancel <strong>{modal?.entry?.customerName}</strong> (party of {modal?.entry?.partySize})?
          </p>
          <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>Reason</Label>
          <Input type="select" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} style={{ borderRadius: 8, border: `1px solid ${C.border}` }}>
            {CANCEL_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </Input>
        </ModalBody>
        <ModalFooter>
          <Button onClick={closeModal} style={{ background: C.white, border: `1px solid ${C.border}`, color: C.textMed, fontWeight: 600 }}>Keep</Button>
          <Button onClick={handleConfirmCancel} style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", fontWeight: 600 }}>Confirm Cancel</Button>
        </ModalFooter>
      </Modal>

      {/* Bulk cancel */}
      <Modal isOpen={modal?.type === "bulkCancel"} toggle={closeModal} centered>
        <ModalHeader toggle={closeModal}>Cancel all active entries</ModalHeader>
        <ModalBody>
          <p style={{ color: C.textMed }}>
            This will cancel {counts.waiting + counts.notified} active entries{selectedBranch ? " at this branch" : ""}.
          </p>
          <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>Reason</Label>
          <Input type="select" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} style={{ borderRadius: 8, border: `1px solid ${C.border}` }}>
            {CANCEL_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </Input>
        </ModalBody>
        <ModalFooter>
          <Button onClick={closeModal} style={{ background: C.white, border: `1px solid ${C.border}`, color: C.textMed, fontWeight: 600 }}>Keep</Button>
          <Button onClick={handleConfirmBulkCancel} style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", fontWeight: 600 }}>Cancel All</Button>
        </ModalFooter>
      </Modal>

      {/* Re-quote */}
      <Modal isOpen={modal?.type === "requote"} toggle={closeModal} centered>
        <ModalHeader toggle={closeModal}>Re-quote active waitlist</ModalHeader>
        <ModalBody>
          <p style={{ color: C.textMed, marginBottom: 12 }}>
            Adjust the quoted wait time for {counts.waiting + counts.notified} active entries{selectedBranch ? " at this branch" : ""}. Use a negative value to reduce.
          </p>
          <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>Delta (minutes)</Label>
          <Input type="number" value={requoteDelta} onChange={(e) => setRequoteDelta(e.target.value)} style={{ borderRadius: 8, border: `1px solid ${C.border}` }} />
        </ModalBody>
        <ModalFooter>
          <Button onClick={closeModal} style={{ background: C.white, border: `1px solid ${C.border}`, color: C.textMed, fontWeight: 600 }}>Cancel</Button>
          <Button onClick={handleConfirmRequote} style={{ background: `linear-gradient(135deg, ${C.blue}, #A8B8FF)`, border: "none", fontWeight: 600 }}>Apply</Button>
        </ModalFooter>
      </Modal>

      {/* Seat */}
      <Modal isOpen={modal?.type === "seat"} toggle={closeModal} centered>
        <ModalHeader toggle={closeModal}>Seat {modal?.entry?.customerName}</ModalHeader>
        <ModalBody>
          <p style={{ color: C.textMed, marginBottom: 12 }}>
            Party of {modal?.entry?.partySize} at {modal?.entry?.Branch?.name || "this branch"}.
          </p>
          <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>Available table</Label>
          <Input type="select" value={seatTableId} onChange={(e) => setSeatTableId(e.target.value)} style={{ borderRadius: 8, border: `1px solid ${C.border}` }}>
            <option value="">Select a table</option>
            {seatTables.map((t) => (
              <option key={t.id} value={t.id}>{t.name} (#{t.tableNumber}) — seats {t.seatingCapacity}</option>
            ))}
          </Input>
          {seatTables.length === 0 && <p style={{ fontSize: 12, color: C.textLight, marginTop: 8 }}>No available tables at this branch.</p>}
        </ModalBody>
        <ModalFooter>
          <Button onClick={closeModal} style={{ background: C.white, border: `1px solid ${C.border}`, color: C.textMed, fontWeight: 600 }}>Cancel</Button>
          <Button onClick={handleConfirmSeat} disabled={!seatTableId || seatLoading} style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", fontWeight: 600 }}>
            {seatLoading ? "Seating..." : "Seat Guest"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Settings */}
      <Modal isOpen={modal?.type === "settings"} toggle={closeModal} centered>
        <ModalHeader toggle={closeModal}>
          Waitlist settings — {branches.find((b) => String(b.id) === String(selectedBranch))?.name || ""}
        </ModalHeader>
        <ModalBody>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div>
              <div style={{ fontWeight: 600, color: C.text }}>Pause waitlist</div>
              <div style={{ fontSize: 12, color: C.textLight }}>Stop accepting new entries right now.</div>
            </div>
            <Input
              type="checkbox"
              checked={settingsForm.waitlistPaused}
              onChange={(e) => setSettingsForm((p) => ({ ...p, waitlistPaused: e.target.checked }))}
              style={{ width: 18, height: 18, position: "relative", margin: 0 }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>Default quoted wait (min)</Label>
            <Input
              type="number"
              min="0"
              placeholder="No default"
              value={settingsForm.waitlistCurrentWaitMinutes}
              onChange={(e) => setSettingsForm((p) => ({ ...p, waitlistCurrentWaitMinutes: e.target.value }))}
              style={{ borderRadius: 8, border: `1px solid ${C.border}` }}
            />
            <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>Auto-applied to new entries (customer self-join and admin add) when no per-entry value is given. Bulk re-quote still adjusts existing entries.</div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>Capacity cap</Label>
            <Input
              type="number"
              min="1"
              placeholder="Unlimited"
              value={settingsForm.waitlistCapacityCap}
              onChange={(e) => setSettingsForm((p) => ({ ...p, waitlistCapacityCap: e.target.value }))}
              style={{ borderRadius: 8, border: `1px solid ${C.border}` }}
            />
            <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>Stop accepting new entries when active queue reaches this number. Leave blank for unlimited.</div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>Quiet from</Label>
              <Input
                type="time"
                value={settingsForm.waitlistQuietFrom}
                onChange={(e) => setSettingsForm((p) => ({ ...p, waitlistQuietFrom: e.target.value }))}
                style={{ borderRadius: 8, border: `1px solid ${C.border}` }}
              />
            </div>
            <div>
              <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>Quiet to</Label>
              <Input
                type="time"
                value={settingsForm.waitlistQuietTo}
                onChange={(e) => setSettingsForm((p) => ({ ...p, waitlistQuietTo: e.target.value }))}
                style={{ borderRadius: 8, border: `1px solid ${C.border}` }}
              />
            </div>
          </div>
          <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>Don't accept new entries during this window. Leave blank to disable.</div>

          <div style={{ borderTop: `1px solid ${C.borderLight}`, marginTop: 20, paddingTop: 16 }}>
            <Label style={{ fontSize: 12, fontWeight: 600, color: C.textMed }}>Auto-release stale tables after (hours)</Label>
            <Input
              type="number"
              min="0"
              max="48"
              placeholder="Default (2)"
              value={settingsForm.tableAutoReleaseHours}
              onChange={(e) => setSettingsForm((p) => ({ ...p, tableAutoReleaseHours: e.target.value }))}
              style={{ borderRadius: 8, border: `1px solid ${C.border}` }}
            />
            <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>Tables stuck in 'occupied' beyond this many hours are auto-released by the system. Set 0 to disable for this branch. Leave blank to use the platform default.</div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button onClick={closeModal} style={{ background: C.white, border: `1px solid ${C.border}`, color: C.textMed, fontWeight: 600 }}>Cancel</Button>
          <Button onClick={handleSaveSettings} style={{ background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`, border: "none", fontWeight: 600 }}>Save</Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default WaitlistList;
